import type {
	InformationGatheringDraft,
	InformationGatheringPatientIdentity,
} from '@feature/information-gathering/information-gathering.model';
import {
	getInformationGatheringEntry,
	type InformationGatheringEntry,
	releaseInformationGatheringState,
} from '@feature/information-gathering/information-gathering-navigation';
import { type ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { LocationScreen } from './location-screen';
import { PatientDetailsScreen } from './patient-details-screen';
import { PhoneScreen } from './phone-screen';
import { RelationshipScreen } from './relationship-screen';
import { SelfDetailsScreen } from './self-details-screen';
import { InformationGatheringJourneyProvider, useInformationGatheringJourney } from './use-information-gathering-journey';

export function ScreenInformationGathering({
	onComplete,
	error,
}: {
	onComplete(
		entry: InformationGatheringEntry,
		draft: InformationGatheringDraft,
		patientIdentity: InformationGatheringPatientIdentity,
	): Promise<boolean>;
	error: ReactNode;
}) {
	const location = useLocation();
	const [entry] = useState(() => getInformationGatheringEntry(location.state));

	useEffect(
		() => () => {
			releaseInformationGatheringState(location.state);
		},
		[location.state],
	);

	return entry === null ? error : <InformationGatheringFlow entry={entry} onComplete={onComplete} error={error} />;
}

function InformationGatheringFlow({
	entry,
	onComplete,
	error,
}: {
	entry: InformationGatheringEntry;
	onComplete(
		entry: InformationGatheringEntry,
		draft: InformationGatheringDraft,
		patientIdentity: InformationGatheringPatientIdentity,
	): Promise<boolean>;
	error: ReactNode;
}) {
	return (
		<InformationGatheringJourneyProvider entry={entry} onComplete={onComplete}>
			{entry.role === 'self' ? (
				<SelfInformationGatheringFlow error={error} />
			) : (
				<RepresentativeInformationGatheringFlow error={error} />
			)}
		</InformationGatheringJourneyProvider>
	);
}

function SelfInformationGatheringFlow({ error }: { error: ReactNode }) {
	const { step } = useInformationGatheringJourney();

	switch (step) {
		case 'self-details':
			return <SelfDetailsScreen />;
		case 'self-contact':
			return <PhoneScreen subject='self' />;
		case 'location':
			return <LocationScreen patientWording={false} />;
		default:
			return error;
	}
}

function RepresentativeInformationGatheringFlow({ error }: { error: ReactNode }) {
	const { step } = useInformationGatheringJourney();

	switch (step) {
		case 'representative-details':
			return <RelationshipScreen />;
		case 'representative-contact':
			return <PhoneScreen subject='representative' />;
		case 'patient-details':
			return <PatientDetailsScreen />;
		case 'patient-contact':
			return <PhoneScreen subject='patient' />;
		case 'location':
			return <LocationScreen patientWording />;
		default:
			return error;
	}
}
